import { useRuleResults } from '../rules/RuleResultsContext';
import { usePriceBreakdown } from '../pricing/PriceContext';
import { formatCurrency } from '../pricing/format';
import { useTranslation } from '../i18n';
import type { LocalizedString } from '../schema';
import { useCombinedSku } from './useCombinedSku';

/**
 * Sticky bottom bar: live total + rule errors. No CTA — Add-to-Cart is the
 * host shop's responsibility (V3 exposes `Configento.finalize()` in Phase 4
 * which a webshop plugin wires to its own cart button).
 *
 * The Gesamtpreis (total) DIV always renders — `component.displayPrice`
 * governs only value-level price suffixes (Aufpreise) and does not affect
 * the total. The admin preview toolbar toggles total visibility via the
 * `body.configento-hide-total` CSS class without touching this component.
 */
export function BottomBar() {
    const { errors, warnings } = useRuleResults();
    const { total, currency, priceIncludesTax } = usePriceBreakdown();
    const { t, locale } = useTranslation();
    const formattedTotal = formatCurrency(total, currency, locale);
    const hasErrors = errors.length > 0;
    const combinedSku = useCombinedSku();

    return (
        <div className="cfg-bottom-bar" data-configento-bottom-bar>
            <div className="cfg-bottom-bar__left">
                <div className="cfg-bottom-bar__price" aria-live="polite">
                    {formattedTotal}
                    <span className="cfg-bottom-bar__price-label">
                        {priceIncludesTax
                            ? t('bottombar.price_incl_tax')
                            : t('bottombar.price_excl_tax')}
                    </span>
                </div>
                {/* Shown only when the admin preview asks for it
                    (body.configento-show-combined-sku). */}
                {combinedSku !== '' && (
                    <div className="cfg-bottom-bar__sku" data-configento-combined-sku>
                        {t('summary.sku')}: {combinedSku}
                    </div>
                )}
                {hasErrors && (
                    <ul className="cfg-bottom-bar__errors" data-configento-error-count={errors.length}>
                        {errors.slice(0, 3).map((err) => (
                            <li key={err.ruleId} className="cfg-bottom-bar__error">
                                {renderErrorMessage(err.message, locale)}
                            </li>
                        ))}
                        {errors.length > 3 && (
                            <li className="cfg-bottom-bar__error">
                                {t('bottombar.errors_more', { n: errors.length - 3 })}
                            </li>
                        )}
                    </ul>
                )}
                {warnings.length > 0 && (
                    <ul className="cfg-bottom-bar__warnings" data-configento-warning-count={warnings.length}>
                        {warnings.slice(0, 3).map((w) => (
                            <li key={w.ruleId} className="cfg-bottom-bar__warning">
                                {renderErrorMessage(w.message, locale)}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function renderErrorMessage(message: string | LocalizedString, locale: string): string {
    if (typeof message === 'string') return message;
    const direct = message[locale];
    if (typeof direct === 'string' && direct.length > 0) return direct;
    for (const v of Object.values(message)) {
        if (typeof v === 'string' && v.length > 0) return v;
    }
    return '';
}
